import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LayoutTypeEnum } from '../models/layout.enums';

export type KpiLayout = 'HORIZONTAL' | 'VERTICAL';

const LAYOUT_KEY = 'nd_layout';
const OVERRIDE_KEY = 'nd_layout_override';
const COMPACT_KEY = 'nd_compact_mode';

const KPI_LAYOUT_KEY = 'kpi_layout';
const KPI_OVERRIDE_KEY = 'kpi_layout_override';

const LEGACY_MIGRATION: Record<string, LayoutTypeEnum> = {
    DEFAULT: LayoutTypeEnum.HORIZONTAL,
    EDITION: LayoutTypeEnum.HORIZONTAL_HIDDEN,
    FLASH: LayoutTypeEnum.HORIZONTAL,
};

@Injectable({ providedIn: 'root' })
export class LayoutService {
    public layout: BehaviorSubject<LayoutTypeEnum>;
    public resized: BehaviorSubject<boolean>;
    public hasSelection$ = new BehaviorSubject<boolean>(false);
    public userOverride$: BehaviorSubject<boolean>;

    public kpiLayout$: BehaviorSubject<KpiLayout>;
    public kpiUserOverride$: BehaviorSubject<boolean>;

    constructor() {
        const raw = localStorage.getItem(LAYOUT_KEY);
        const migrated = raw ? (LEGACY_MIGRATION[raw] ?? raw) : null;
        const valid = Object.values(LayoutTypeEnum) as string[];
        const initial: LayoutTypeEnum = migrated && valid.includes(migrated)
            ? migrated as LayoutTypeEnum
            : LayoutTypeEnum.HORIZONTAL;
        this.layout = new BehaviorSubject<LayoutTypeEnum>(initial);

        const override = localStorage.getItem(OVERRIDE_KEY) === 'true';
        this.userOverride$ = new BehaviorSubject<boolean>(override);

        const compact = localStorage.getItem(COMPACT_KEY) === 'true';
        this.resized = new BehaviorSubject<boolean>(compact);
        if (compact) {
            document.documentElement.classList.add('small');
        }

        const kpiRaw = localStorage.getItem(KPI_LAYOUT_KEY) as KpiLayout | null;
        this.kpiLayout$ = new BehaviorSubject<KpiLayout>(
            kpiRaw === 'HORIZONTAL' || kpiRaw === 'VERTICAL' ? kpiRaw : 'HORIZONTAL',
        );
        const kpiOverride = localStorage.getItem(KPI_OVERRIDE_KEY) === 'true';
        this.kpiUserOverride$ = new BehaviorSubject<boolean>(kpiOverride);
    }

    public changeLayout(layout: LayoutTypeEnum, byUser = false): void {
        localStorage.setItem(LAYOUT_KEY, layout);
        if (byUser) {
            localStorage.setItem(OVERRIDE_KEY, 'true');
            this.userOverride$.next(true);
        }
        this.layout.next(layout);
    }

    public changeKpiLayout(layout: KpiLayout, byUser = false): void {
        localStorage.setItem(KPI_LAYOUT_KEY, layout);
        if (byUser) {
            localStorage.setItem(KPI_OVERRIDE_KEY, 'true');
            this.kpiUserOverride$.next(true);
        }
        this.kpiLayout$.next(layout);
    }

    public resize(): void {
        const next = !this.resized.getValue();
        this.resized.next(next);
        localStorage.setItem(COMPACT_KEY, String(next));
        document.documentElement.classList.toggle('small', next);
    }
}
